import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../services/api';
import { 
  FileText, 
  Save, 
  History, 
  RefreshCw, 
  Eye, 
  EyeOff,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

export function AdminPrompts() {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['seo_analysis', 'seo_actions', 'summary']));
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchPrompts();
  }, []);

  // Admin access is already checked by AdminRoute component

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/admin/prompts', { method: 'GET' });
      
      if (response.success) {
        const promptsList = response.prompts || [];
        setPrompts(promptsList);
        
        // Select first prompt by default
        if (promptsList.length > 0 && !selectedPrompt) {
          selectPrompt(promptsList[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      showMessage('Failed to load prompts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setEditingContent(prompt.content);
    setShowHistory(false);
  };

  const savePrompt = async () => {
    if (!selectedPrompt) return;

    try {
      setSaving(true);
      const response = await apiCall(`/admin/prompts/${selectedPrompt.key}`, {
        method: 'PUT',
        body: {
          content: editingContent,
          name: selectedPrompt.name,
          description: selectedPrompt.description,
          is_active: selectedPrompt.is_active
        }
      });

      if (response.success) {
        showMessage('Prompt saved successfully!', 'success');
        
        // Update local state
        setPrompts(prompts.map(p => 
          p.key === selectedPrompt.key ? response.prompt : p
        ));
        setSelectedPrompt(response.prompt);
      }
    } catch (error) {
      console.error('Failed to save prompt:', error);
      showMessage('Failed to save prompt', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fetchHistory = async (promptKey) => {
    try {
      const response = await apiCall(`/admin/prompts/${promptKey}/history`, {
        method: 'GET'
      });
      
      if (response.success) {
        setHistory(response.history);
        setShowHistory(true);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
      showMessage('Failed to load history', 'error');
    }
  };

  const revertToVersion = async (historyId) => {
    if (!selectedPrompt) return;

    try {
      const response = await apiCall(
        `/admin/prompts/${selectedPrompt.key}/revert/${historyId}`,
        { method: 'POST' }
      );

      if (response.success) {
        showMessage('Reverted to previous version', 'success');
        setSelectedPrompt(response.prompt);
        setEditingContent(response.prompt.content);
        setShowHistory(false);
        fetchPrompts();
      }
    } catch (error) {
      console.error('Failed to revert:', error);
      showMessage('Failed to revert to version', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const groupedPrompts = (prompts || []).reduce((acc, prompt) => {
    if (!acc[prompt.category]) {
      acc[prompt.category] = [];
    }
    acc[prompt.category].push(prompt);
    return acc;
  }, {});

  const filteredPrompts = activeFilter === 'all' 
    ? (prompts || []) 
    : (prompts || []).filter(p => activeFilter === 'active' ? p.is_active : !p.is_active);

  const categoryNames = {
    seo_analysis: 'SEO Analysis',
    seo_actions: 'SEO Actions',
    summary: 'Executive Summary'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading prompts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-foreground">Prompt Management</h1>
            <p className="mt-2 text-muted-foreground">
              Edit and manage AI prompts used throughout the application
            </p>
          </div>
        </div>
      </div>

      {message && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="bg-card rounded-lg shadow">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Prompts</h2>
                
                {/* Filter buttons */}
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setActiveFilter('all')}
                    className={`px-3 py-1 text-sm rounded ${
                      activeFilter === 'all'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                    }`}
                  >
                    All ({(prompts || []).length})
                  </button>
                  <button
                    onClick={() => setActiveFilter('active')}
                    className={`px-3 py-1 text-sm rounded ${
                      activeFilter === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setActiveFilter('inactive')}
                    className={`px-3 py-1 text-sm rounded ${
                      activeFilter === 'inactive'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                    }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>
              
              <div className="p-4 space-y-2">
                {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => (
                  <div key={category}>
                    <button
                      onClick={() => toggleCategory(category)}
                      className="flex items-center justify-between w-full text-left p-2 hover:bg-background rounded"
                    >
                      <span className="font-medium text-card-foreground">
                        {categoryNames[category] || category}
                      </span>
                      {expandedCategories.has(category) ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedCategories.has(category) && (
                      <div className="ml-4 mt-1 space-y-1">
                        {categoryPrompts
                          .filter(p => activeFilter === 'all' || 
                            (activeFilter === 'active' ? p.is_active : !p.is_active))
                          .map(prompt => (
                            <button
                              key={prompt.key}
                              onClick={() => selectPrompt(prompt)}
                              className={`w-full text-left p-2 rounded text-sm flex items-center justify-between ${
                                selectedPrompt?.key === prompt.key
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'hover:bg-background text-muted-foreground'
                              }`}
                            >
                              <span className="truncate">{prompt.name}</span>
                              {!prompt.is_active && <EyeOff className="h-3 w-3" />}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            {selectedPrompt ? (
              <div className="bg-card rounded-lg shadow">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground">
                        {selectedPrompt.name}
                      </h2>
                      <p className="mt-1 text-muted-foreground">{selectedPrompt.description}</p>
                      <div className="mt-2 flex items-center space-x-4 text-sm">
                        <span className="text-muted-foreground">
                          Key: <code className="bg-gray-100 px-2 py-1 rounded">
                            {selectedPrompt.key}
                          </code>
                        </span>
                        <span className={`flex items-center ${
                          selectedPrompt.is_active ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {selectedPrompt.is_active ? (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-4 w-4 mr-1" />
                              Inactive
                            </>
                          )}
                        </span>
                        <span className="text-muted-foreground">
                          Version: {selectedPrompt.version}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => fetchHistory(selectedPrompt.key)}
                        className="px-4 py-2 text-card-foreground bg-gray-100 rounded hover:bg-gray-200 flex items-center space-x-2"
                      >
                        <History className="h-4 w-4" />
                        <span>History</span>
                      </button>
                      <button
                        onClick={savePrompt}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                      >
                        {saving ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {selectedPrompt.variables && selectedPrompt.variables.length > 0 && (
                    <div className="mb-4 p-3 bg-accent rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Variables:</strong> {selectedPrompt.variables.join(', ')}
                      </p>
                    </div>
                  )}

                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="w-full h-96 p-4 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter prompt content..."
                  />

                  <div className="mt-4 text-sm text-muted-foreground">
                    <p>Last updated: {new Date(selectedPrompt.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select a prompt from the sidebar to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Version History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-400 hover:text-muted-foreground"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {history.map((entry) => (
                  <div key={entry.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium">Version {entry.version}</span>
                        <span className="text-muted-foreground text-sm ml-4">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      <button
                        onClick={() => revertToVersion(entry.id)}
                        className="px-3 py-1 text-sm bg-gray-100 text-card-foreground rounded hover:bg-gray-200"
                      >
                        Revert to this version
                      </button>
                    </div>
                    {entry.change_note && (
                      <p className="text-sm text-muted-foreground mb-2">{entry.change_note}</p>
                    )}
                    <pre className="bg-background p-3 rounded text-xs overflow-x-auto">
                      {entry.content.substring(0, 300)}...
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}